<template>
    <v-card flat>
        <v-overlay :value="loadingProductData">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
        <v-toolbar dense color="bal-gray">
            <v-toolbar-title>{{ activeProduct.name }}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn
                v-if="hasPermissionForAction(PermissionsConstants.PRODUCT, PermissionsConstants.EDIT)"
                dark
                color="bal-red"
                @click="editProduct"
                :disabled="tabName === 'Files'"
            >
                <v-icon>edit</v-icon>
                {{ "Edit" | t }}
            </v-btn>
            <v-menu offset-y bottom left>
                <template v-slot:activator="{ on }">
                    <v-btn color="bal-dark-gray" class="px-2 ml-2 mx-2" style="min-width: 0" dark v-on="on">
                        <v-icon>expand_more</v-icon>
                    </v-btn>
                </template>
                <v-list>
                    <v-list-item @click="handleDuplicateClick" v-if="user.role === 'superadmin'">
                        <v-list-item-title>{{ "Duplicate" | t }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                        @click="handleWordpressClick"
                        v-if="hasPermissionForAction(PermissionsConstants.PRODUCT, PermissionsConstants.EXPORT)"
                    >
                        <v-list-item-title>{{ "Send to Wordpress" | t }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                        @click="handleDeleteClick"
                        v-if="hasPermissionForAction(PermissionsConstants.PRODUCT, PermissionsConstants.DELETE)"
                    >
                        <v-list-item-title style="color: red">{{ "Delete" | t }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
            <v-btn @click="closeDrawer()" icon>
                <v-icon>close</v-icon>
            </v-btn>
        </v-toolbar>
        <v-tabs v-model="tab" background-color="bal-gray" color="bal-red">
            <v-tab style="text-transform: none !important">
                {{ "General info" | t }}
            </v-tab>
            <v-tab-item>
                <general-info-tab />
            </v-tab-item>
            <v-tab style="text-transform: none !important">
                {{ "Specification" | t }}
            </v-tab>
            <v-tab-item>
                <spec-tab />
            </v-tab-item>
            <v-tab style="text-transform: none !important">
                {{ "Codes" | t }}
            </v-tab>
            <v-tab-item>
                <codes-tab />
            </v-tab-item>
            <v-tab style="text-transform: none !important">
                {{ "Package" | t }}
            </v-tab>
            <v-tab-item>
                <package-tab />
                <logistics-tab />
                <package-other-tab />
            </v-tab-item>
            <v-tab style="text-transform: none !important">
                {{ "Quantity" | t }}
            </v-tab>
            <v-tab-item>
                <quantity-tab />
            </v-tab-item>
            <v-tab style="text-transform: none !important">
                {{ "Files" | t }}
            </v-tab>
            <v-tab-item>
                <file-tab />
            </v-tab-item>
            <v-tab style="text-transform: none !important">
                {{ "Production info" | t }}
            </v-tab>
            <v-tab-item>
                <production-info-tab />
            </v-tab-item>
        </v-tabs>
    </v-card>
</template>

<script>
import { mapState, mapMutations, mapGetters } from "vuex";

import GeneralInfoTab from "./product-tab-items/general-info-tab.vue";
import PackageTab from "./product-tab-items/package-tab.vue";
import LogisticsTab from "./product-tab-items/logistics-tab.vue";
import CodesTab from "./product-tab-items/codes-tab.vue";
import SpecTab from "./product-tab-items/specification-tab.vue";
import FileTab from "./product-tab-items/file-tab.vue";
import QuantityTab from "./product-tab-items/quantity-tab.vue";
import ProductionInfoTab from "./product-tab-items/production-info-tab.vue";

import Store from "../../store";
import PackageOtherTab from "@/pages/products/product-tab-items/package-other-tab.vue";
import { PermissionsConstants } from "@/permissions";

export default {
    name: "product-view",
    components: {
        PackageOtherTab,
        GeneralInfoTab,
        PackageTab,
        CodesTab,
        SpecTab,
        FileTab,
        LogisticsTab,
        QuantityTab,
        ProductionInfoTab,
    },
    data() {
        return {
            tab: 0,
            tabs: ["General info", "Specification", "Codes", "Package", "Quantity", "Files"],
        };
    },
    mounted() {
        document.addEventListener("keydown", this.nextTab);
        Store.dispatch("ProductStore/getProductData", this.$route.params.productId);
        if (this.$route.params.tab) {
            this.tab = this.tabs.indexOf(this.$route.params.tab);
        }
    },
    destroyed() {
        document.removeEventListener("keydown", this.nextTab);
    },
    computed: {
        ...mapState(["user"]),
        ...mapGetters(["hasPermissionForAction"]),
        PermissionsConstants() {
            return PermissionsConstants;
        },
        ...mapState("ProductStore", ["activeProduct", "loadingProductData", "searchProductString", "tableOptions"]),
        tabName() {
            return this.tabs[this.tab];
        },
    },
    methods: {
        ...mapMutations("ProductStore", ["resetActiveProduct"]),
        closeDrawer() {
            this.resetActiveProduct();
            this.$router.push({ name: "product-list" });
        },
        editProduct() {
            this.$router.push({
                name: "product-edit",
                params: { productId: this.$route.params.productId, tab: this.tabs[this.tab] },
            });
        },
        nextTab(e) {
            if (e.keyCode === 69 && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                this.editProduct();
            }
            if (e.key === "ArrowLeft") {
                if (this.tab > 0) {
                    this.tab -= 1;
                }
            } else if (e.key === "ArrowRight") {
                if (this.tab + 1 < this.tabs.length) {
                    this.tab += 1;
                }
            }
        },
        async handleDuplicateClick() {
            if (confirm("Are you sure you want to make a copy?")) {
                const promise = new Promise((resolve, reject) => {
                    let resp = Store.dispatch("ProductStore/createDuplicate", {
                        product_id: this.activeProduct.id,
                    });
                    if (resp) {
                        resolve(resp);
                    } else {
                        reject(Error("Error"));
                    }
                });

                promise.then(
                    (result) => {
                        this.$router.push({
                            name: "product-view",
                            params: { productId: result.id, tab: this.tabs[this.tab] },
                        });
                        Store.dispatch("ProductStore/getProductList", {
                            options: this.tableOptions,
                            searchStrings: this.searchProductStrings,
                        });
                    },
                    (err) => {
                        console.log(err);
                    }
                );
            }
        },
        handleDeleteClick() {
            if (confirm("Are you sure you want to delete this product?")) {
                const promise = new Promise((resolve, reject) => {
                    let resp = Store.dispatch("ProductStore/deleteProduct", {
                        product_id: this.activeProduct.id,
                    });
                    if (resp) {
                        resolve(resp);
                    } else {
                        reject(Error("Error"));
                    }
                });

                promise.then(
                    (result) => {
                        this.$router.push({
                            name: "product-list",
                        });
                        Store.dispatch("ProductStore/getProductList", {
                            options: this.tableOptions,
                            searchStrings: this.searchProductStrings,
                        });
                    },
                    (err) => {
                        console.log(err);
                    }
                );
            }
        },
        async handleWordpressClick() {
            if (confirm("Are you sure you want to send this product?")) {
                const response = await Store.dispatch("ProductStore/sendProductToWordpress", {
                    product_id: this.activeProduct.id,
                });
            }
        },
    },
};
</script>

<style scoped></style>
